import { useEffect, useMemo, useState, useCallback } from "react";
import DataTable from "../../components/common/DataTable";
import { getTransactions } from "../../services/transactionService";
import { getWalletDashboard } from "../../services/walletService";
import { handleApiError } from "../../utils/handleApiError";
// import TransactionDetailModal from "../../components/common/TransactionDetailModal";
import FilterBar from "./filterbar";
import api from "../../services/api";
import { toast } from "react-toastify";

const toNumber = (value) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const currencyFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  minimumFractionDigits: 2,
});

const formatCurrency = (value) => {
  return currencyFormatter.format(toNumber(value));
};

const getServiceName = (tx) => {
  const s = tx.service_name || tx.service || tx.service_type || tx.transaction_type || tx.mode;
  if (!s) return "N/A";
  if (typeof s === "object") return s?.name || s?.slug || "N/A";
  return String(s);
};

const getTransactionId = (tx) => tx.transaction_id || tx.txn_id || tx.id || "-";

const statusStyle = (status) => {
  const s = String(status || "").toUpperCase();
  if (s === "SUCCESS") return "bg-emerald-100 text-emerald-600";
  if (s === "PENDING") return "bg-amber-100 text-amber-600";
  return "bg-red-100 text-red-600";
};

export default function Dashboard() {
  const [walletData, setWalletData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    has_more: false,
    total: 0,
  });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [txError, setTxError] = useState(null);
  const [loadingWallet, setLoadingWallet] = useState(true);
  const [loadingTransactions, setLoadingTransactions] = useState(true);
  const [selectedTxId, setSelectedTxId] = useState(null);
  const [myServices, setMyServices] = useState([]);
  const [selectedService, setSelectedService] = useState("");

  // FilterBar states
  const [searchInput, setSearchInput] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  // const [statusFilter, setStatusFilter] = useState("");
  const [appliedStatus, setAppliedStatus] = useState("");
  const [dateError, setDateError] = useState("");
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    getWalletDashboard()
      .then((res) => {
        setWalletData(res.data?.data || res.data || null);
      })
      .catch(handleApiError)
      .finally(() => setLoadingWallet(false));
  }, []);

useEffect(() => {
  api
    .get("/services/my")
    .then((res) => {
      const services = res.data?.data || [];

      setMyServices(
        Array.isArray(services) ? services : []
      );
    })
    .catch((err) => {
      console.log(err);
    });
}, []);

const serviceOptions = myServices.map((service) => ({
  label: service.name,
  value: service.service_id,
}));
 const fetchTransactions = useCallback((pg, lmt, srch, from, to, status, serviceId) => {
    setLoadingTransactions(true);
    setTxError(null);

    getTransactions({
      page: pg,
      limit: lmt,
      search: srch || undefined,
      from_date: from || undefined,
      to_date: to || undefined,
      status: status || undefined,
      service_id: serviceId || undefined,
    })
      .then((res) => {
        const payload = res.data?.data || res.data || {};

        const rows = Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload?.transactions)
          ? payload.transactions
          : Array.isArray(payload)
          ? payload
          : [];

        const p = payload?.pagination || payload;

        const currentPage = Number(p?.current_page) || pg;
        const lastPage = Number(p?.last_page) || 1;

        setTransactions(rows);

        setPagination({
          current_page: currentPage,
          last_page: lastPage,
          has_more: Boolean(
            p?.has_more ?? currentPage < lastPage
          ),
          total: Number(p?.total) || rows.length,
        });
      })
      .catch((err) => {
        handleApiError(err);
        setTxError("Failed to load transactions.");
      })
      .finally(() => setLoadingTransactions(false));
  },
  []
);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      setSearch(searchInput.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    fetchTransactions( 
        page,
        limit,
        search,
        fromDate,
        toDate,
        appliedStatus,
        selectedService
      );
    }, [page, limit, search,  fromDate, toDate, appliedStatus, selectedService, fetchTransactions]);

const validateDates = useCallback((from, to) => {
  if (from && to && from > to) {
    setDateError("From date cannot be after To date.");
  } else {
    setDateError("");
  }
}, []);

const handleExport = async (format = "csv") => {
  setExporting(true);
  try {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (fromDate) params.append('from_date', fromDate);
    if (toDate) params.append('to_date', toDate);
    if (appliedStatus) params.append('status', appliedStatus);
    if (selectedService) params.append('service_id', selectedService);
    params.append('format', format);
    params.append('limit', 10000);
    params.append('page', 1);

    const response = await api.get(`/transactions/export?${params.toString()}`, {
      responseType: 'blob'
    });

    // Server ne file ki jagah JSON queued message bheja
    const contentType = response.headers?.['content-type'] || '';
    if (contentType.includes('application/json')) {
      const text = await response.data.text();
      const json = JSON.parse(text);
      toast.info(json.message || 'Export queued. Check Reports section to download.');
      return;
    }

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const ext = format === "excel" ? "xlsx" : "csv";
    const timestamp = new Date().toISOString().split('T')[0];
    let filename = `transactions_${timestamp}`;
    if (fromDate) filename += `_from_${fromDate}`;
    if (toDate) filename += `_to_${toDate}`;
    if (appliedStatus) filename += `_${appliedStatus}`;
    if (selectedService) {
      const serviceName = myServices.find(s => s.service_id === selectedService)?.name || selectedService;
      filename += `_${serviceName}`;
    }
    filename += `.${ext}`;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    toast.success('Export completed successfully!');
  } catch (error) {
    console.error('Export failed:', error);
    toast.error('Export failed. Please try again.');
  } finally {
    setExporting(false);
  }
};

  const handleLimitChange = (val) => {
    setLimit(val);
    setPage(1);
  };

  const columns = useMemo(
    () => [
      {
        id: "sr",
        header: "Sr No",
        size: 60,
        Cell: ({ row }) => (
          <span className="text-[13px] text-[#4C444E] font-semibold">
            {(page - 1) * limit + row.index + 1}
          </span>
        ),
      },
      {
        accessorKey: "transaction_id",
        header: "Transaction ID",
        Cell: ({ row }) => (
          <span className="text-[13px] font-bold text-[#4B2364]">{getTransactionId(row.original)}</span>
        ),
      },
      {
        accessorKey: "created_at",
        header: "Date & Time",
        Cell: ({ row }) => {
          const raw = row.original.created_at || row.original.date || null;
          const dt = raw ? new Date(raw) : null;
          return (
            <div>
              <div className="text-[13px] font-semibold text-[#1E0A3C]">
                {dt ? dt.toLocaleDateString() : "-"}
              </div>
              <div className="text-[11px] text-gray-500 mt-0.5">
                {dt ? dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: "utr",
        header: "UTR",
        Cell: ({ row }) => (
          <span className="text-[13px] text-[#4C444E]">{row.original.utr || "-"}</span>
        ),
      },
      {
        accessorKey: "ip_address",
        header: "IP Address",
        Cell: ({ row }) => (
          <span className="text-[13px] text-[#4C444E]">{row.original.ip_address || "-"}</span>
        ),
      },
      {
        accessorKey: "service_name",
        header: "Service Type",
        Cell: ({ row }) => <span className="text-[13px] font-bold text-[#1E0A3C]">{getServiceName(row.original)}</span>,
      },
      {
        accessorKey: "amount",
        header: "Amount",
        Cell: ({ row }) => (
          <span className="text-sm font-bold text-[#1E0A3C]">{formatCurrency(row.original.amount)}</span>
        ),
      },
     {
          accessorKey: "commission",
          header: "Commission/Charges",
          Cell: ({ row }) => {
            const commission = toNumber(row.original.commission ?? row.original.service_commission ?? 0);
            const service = getServiceName(row.original).toLowerCase();
            const isPayin = service.includes("payin") || service.includes("pay_in");
            const isPayout = service.includes("payout") || service.includes("pay_out");
            const isZero = commission === 0;
            
            // ✅ Charges are ALWAYS deducted (negative impact)
            const color = isZero ? "text-gray-400" : "text-red-500"; // Always red for deductions
            const prefix = isZero ? "" : "- "; // Always show minus for charges
            
            return (
              <span className={`text-[13px] font-bold ${color}`}>
                {prefix}{formatCurrency(commission)}
              </span>
            );
          },
        },
      {
        accessorKey: "net_amount",
        header: "Net Amount",
        Cell: ({ row }) => (
          <span className="text-[13px] font-bold text-[#1E0A3C]">
            {formatCurrency(row.original.net_amount ?? row.original.amount ?? 0)}
          </span>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        Cell: ({ row }) => (
          <span className={`px-3 py-1.5 rounded-full text-[11px] font-extrabold tracking-wide ${statusStyle(row.original.status)}`}>
            {String(row.original.status || "UNKNOWN").toUpperCase()}
          </span>
        ),
      },
      // {
      //   id: "actions",
      //   header: "Actions",
      //   Cell: ({ row }) => (
      //     <button
      //       onClick={() => setSelectedTxId(getTransactionId(row.original))}
      //       className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold text-[#4B2364] bg-[#F3E8FF] hover:bg-[#E9D5FF] border-none cursor-pointer transition-colors"
      //     >
      //       <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      //         <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      //         <circle cx="12" cy="12" r="3" />
      //       </svg>
      //       View
      //     </button>
      //   ),
      // },
    ],
    [page, limit]
  );
  const creditWalletBalance = walletData?.payin?.total_amount ?? 0;
  const debitWalletBalance = walletData?.payout?.total_amount ?? 0;

  return (
    <div className="flex-1 px-3 sm:px-6 md:px-8 py-4 sm:py-6 overflow-y-auto">
      {selectedTxId && (
        <TransactionDetailModal transactionId={selectedTxId} onClose={() => setSelectedTxId(null)} />
      )}
      {/* Wallet Summary Cards */}
      <div className="text-[26px] font-bold text-[#34094D] mb-4">
        Today's Overview
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">

{/* PayIn Card */}
<div className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#7B4D97] via-[#5A2D78] to-[#3D1658] p-5 sm:p-6 shadow-[0_10px_40px_rgba(75,35,100,0.25)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(75,35,100,0.35)]">
  <div className="absolute -top-6 -right-6 w-28 h-28 border-[18px] border-white/5 rounded-3xl rotate-12" />
  <div className="absolute right-10 top-12 w-16 h-16 border-[14px] border-white/[0.04] rounded-2xl" />

  <div className="relative z-10">
    <div className="flex items-center gap-3 mb-3">
      <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
        <svg className="w-5 h-5 text-[#EBDCFF]" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
          <path d="M12 19V5M5 12l7-7 7 7" />
        </svg>
      </div>
      <p className="text-[13px] font-semibold tracking-wide text-[#EBDCFF] uppercase">
        PayIn Total Amount
      </p>
    </div>
    <h2 className="text-[30px] sm:text-[38px] font-extrabold tracking-tight text-white">
      {loadingWallet ? "..." : formatCurrency(creditWalletBalance)}
    </h2>
    <div className="mt-4 inline-flex items-center text-[14px] font-semibold text-[#EBDCFF] gap-4">
      <span>Total Charges:</span>
      <span className="ml-1 font-bold text-red-400">
        {formatCurrency(walletData?.payin?.total_charge ?? 0)}
      </span>
    </div>
  </div>
</div>

{/* PayOut Card */}
<div className="group relative overflow-hidden rounded-2xl border border-[#ECE7F2] bg-white p-5 sm:p-6 shadow-[0_10px_30px_rgba(75,35,100,0.08)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(75,35,100,0.12)]">
  <div className="absolute -top-10 -right-10 h-40 w-40 rounded-full bg-gradient-to-br from-[#F4ECFF] to-transparent" />

  <div className="relative z-10">
    <div className="flex items-center gap-3 mb-3">
      <div className="w-10 h-10 rounded-xl bg-[#F4ECFF] flex items-center justify-center shrink-0">
        <svg className="w-5 h-5 text-[#7B4D97]" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
          <path d="M12 5v14M19 12l-7 7-7-7" />
        </svg>
      </div>
      <p className="text-[13px] font-semibold tracking-wide text-[#6B5A75] uppercase">
        PayOut Total Amount
      </p>
    </div>
    <h2 className="text-[34px] sm:text-[38px] font-extrabold tracking-tight text-[#2D0A45]">
      {loadingWallet ? "..." : formatCurrency(debitWalletBalance)}
    </h2>
    <div className="mt-4 flex items-center text-[14px] text-[#6B5A75] font-semibold gap-4">
      <span>Total Charges:</span>
      <span className="ml-1 font-bold text-red-600">
        {formatCurrency(walletData?.payout?.total_charge ?? 0)}
      </span>
    </div>
  </div>
</div>

{/* Balance Card */}
<div className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#4B2364] via-[#37114F] to-[#220A35] p-5 sm:p-6 shadow-[0_10px_40px_rgba(45,16,64,0.28)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(45,16,64,0.38)]">
  <div className="absolute -top-6 -right-6 w-28 h-28 border-[18px] border-white/5 rounded-3xl rotate-12" />
  <div className="absolute right-10 top-12 w-16 h-16 border-[14px] border-white/[0.04] rounded-2xl" />

  <div className="relative z-10">
    <div className="flex items-center gap-3 mb-3">
      <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
        <svg className="w-5 h-5 text-[#E9D8FD]" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
          <rect x="2" y="7" width="20" height="14" rx="2" />
          <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
          <line x1="12" y1="12" x2="12" y2="16" />
          <line x1="10" y1="14" x2="14" y2="14" />
        </svg>
      </div>
      <p className="text-[13px] font-semibold tracking-wide text-[#E9D8FD] uppercase">
        Balance
      </p>
    </div>
    <h2 className="text-[34px] sm:text-[38px] font-extrabold tracking-tight text-white">
      {loadingWallet ? "..." : formatCurrency(walletData?.wallet?.balance ?? 0)}
    </h2>
    <div className="mt-4 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-[14px] font-medium text-[#E9D8FD]">Hold Balance</span>
        <span className="text-[14px] font-bold text-yellow-300">
          {formatCurrency(walletData?.wallet?.locked_balance ?? 0)}
        </span>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-[14px] font-medium text-[#E9D8FD]">Available Balance</span>
        <span className="text-[14px] font-bold text-emerald-200">
          {formatCurrency(walletData?.wallet?.available_balance ?? 0)}
        </span>
      </div>
       <div className="flex items-center justify-between">
        <span className="text-[14px] font-medium text-[#E9D8FD]">Unsettled Balance</span>
        <span className="text-[14px] font-bold text-emerald-200">
          {formatCurrency(walletData?.wallet?.unsettled_balance ?? 0)}
        </span>
      </div>
    </div>
  </div>
</div>

      </div>

     <FilterBar
        searchText={searchInput}
        setSearchText={setSearchInput}
        fromDate={fromDate}
        setFromDate={setFromDate}
        toDate={toDate}
        setToDate={setToDate}
        appliedStatus={appliedStatus}
        setAppliedStatus={setAppliedStatus}
        setPage={setPage}
        // applyFilter={applyFilter}
        handleExport={handleExport}
        exporting={exporting}
        dateError={dateError}
        validateDates={validateDates}
        serviceOptions={serviceOptions}
        selectedService={selectedService}
        setSelectedService={setSelectedService}
      />

      <div className="bg-white rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.03)] mb-6">
        <div className="flex flex-col mt-4 sm:flex-row sm:justify-between p-4 sm:p-6 md:p-8 items-start sm:items-center gap-4">
          <div>
            <h2 className="text-[24px] font-bold text-[#34094D] mb-1">Transaction Ledger</h2>
            <p className="text-sm text-[#4C444E]">Real-time records for all service operations</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {/* Limit */}
            <select
              value={limit}
              onChange={(e) => handleLimitChange(Number(e.target.value))}
              className="bg-[#F3F4F5] rounded-[10px] px-3 py-2 text-[13px] text-[#191C1D] border-none outline-none cursor-pointer"
            >
              {[10, 25, 50].map((l) => <option key={l} value={l}>Show {l}</option>)}
            </select>
          </div>
        </div>

        {txError ? (
          <div className="text-center py-10 text-red-500 text-sm">{txError}</div>
        ) : (
          <>
            {loadingTransactions && (
              <div className="px-8 pb-3 text-xs text-gray-500">
                {transactions.length > 0 ? "Refreshing data..." : "Loading transactions..."}
              </div>
            )}
            <DataTable columns={columns} data={transactions} />
            {!loadingTransactions && transactions.length === 0 && (
              <div className="text-center py-10 text-gray-400 text-sm">No data found</div>
            )}
          </>
        )}
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between px-4 sm:px-6 py-4 gap-3">
        <span className="text-xs text-[#4C444E]">
          {pagination.total > 0
            ? `Showing ${(page - 1) * limit + 1}–${Math.min(page * limit, pagination.total)} of ${pagination.total} records`
            : "Showing 0 records"}
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className={`w-8 h-8 flex items-center justify-center rounded-lg border border-[#CEC3D0] ${page === 1 ? "bg-gray-50 text-gray-300 cursor-not-allowed" : "bg-white text-[#4C444E] hover:bg-gray-50"
              }`}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <span className="text-xs text-[#4C444E]">
            {pagination.current_page} / {pagination.last_page}
          </span>
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={!pagination.has_more}
            className={`w-8 h-8 flex items-center justify-center rounded-lg border border-[#CEC3D0] ${!pagination.has_more
                ? "bg-gray-50 text-gray-300 cursor-not-allowed"
                : "bg-white text-[#4C444E] hover:bg-gray-50"
              }`}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
